import React, {useEffect, useId, useRef, useState} from 'react'

type Option = {value: string; label: string}

// Filter button with a checkbox list (several markets, several people).
// Esc or a click outside closes it; focus returns to the button.
export const MultiSelectFilter: React.FC<{
    label: string
    values: string[] | undefined
    options: Option[]
    onChange: (values: string[] | undefined) => void
    align?: 'left' | 'right'
}> = ({label, values = [], options, onChange, align = 'left'}) => {
    const [open, setOpen] = useState(false)
    const root = useRef<HTMLDivElement>(null)
    const button = useRef<HTMLButtonElement>(null)
    const listId = useId()

    useEffect(() => {
        if (!open) return
        const onPointer = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false) }
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            e.stopPropagation()
            setOpen(false)
            button.current?.focus()
        }
        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey, true)
        }
    }, [open])

    const toggle = (value: string) => {
        const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
        onChange(next.length ? next : undefined)
    }

    return (
        <div ref={root} className="relative">
            <button ref={button} type="button" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((o) => !o)}
                    className={`h-9 pl-3 pr-2.5 rounded-full border bg-white text-[13px] font-medium text-neutralBlack-900 inline-flex items-center gap-1.5 hover:border-neutralBlack-400 transition-colors duration-200 ease-out ${values.length ? 'border-neutralBlack-900' : 'border-neutralBlack-200'}`}>
                <span>{label}</span>
                {values.length > 0 && (
                    <span className="min-w-[18px] h-[18px] px-[5px] rounded-full bg-neutralBlack-900 text-white text-[11px] font-semibold inline-flex items-center justify-center">
                        {values.length}
                    </span>
                )}
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            {open && (
                <fieldset id={listId}
                          className={`absolute top-[42px] ${align === 'right' ? 'right-0' : 'left-0'} z-30 min-w-[220px] max-h-[320px] overflow-y-auto bg-white border border-neutralBlack-200 rounded-xl shadow-lg p-1.5`}>
                    <legend className="sr-only">{label}</legend>
                    {options.map((o) => (
                        <label key={o.value}
                               className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-neutralBlack-900 cursor-pointer hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                            <input type="checkbox" checked={values.includes(o.value)} onChange={() => toggle(o.value)}
                                   className="accent-neutralBlack-900"/>
                            {o.label}
                        </label>
                    ))}
                </fieldset>
            )}
        </div>
    )
}
